// useAnalytics — thin Vue layer over the `analyticsService` singleton.
//
// Responsibilities (ANALYTICS_PLAN → "useAnalytics composable"):
//   * Provide the call-site API: `trackImpression`, `trackInteraction`,
//     `startFlow`.
//   * Gate every track call on consent (`null`/`true`/`false`).
//   * Lazy-prompt the consent modal the first time tracking would happen
//     against a `null` consent. Idempotent per session.
//   * Reconcile anon vs authed consent on sign-in: server wins when
//     non-null; otherwise the locally-stored answer gets propagated up.
//
// Module-level state (consent, modal-shown flag) is shared across all
// `useAnalytics()` instances by design — there's one decision per user
// per session, not one per component tree.

import { ref, watch } from 'vue';

import {
    AnalyticsEventType,
    analyticsService,
    type AnalyticsService,
} from '../services/AnalyticsService';
import { getUserPreferences, setAnalyticsConsent } from '../services/api/userPreferencesService';
import { stitchIdentity } from '../services/api/analyticsService';
import { useUserStore } from '../stores/userStore';
import { useModal } from '../modals/useModal';
import { ModalCode } from '../modals/ModalCode';
import { logError } from '../utils/logger';

const ANON_CONSENT_KEY = 'analytics:consent';
// `'deferred'` means the user saw the prompt and dismissed it without
// answering. Local-only marker: never written to the server (which is strictly
// boolean | null). It keeps `consent.value === null` so a later explicit answer
// can still be captured, while preventing the modal from re-popping on every
// reload until they do answer.
type AnonConsentString = 'granted' | 'declined' | 'deferred';

// Discord login is an OAuth round-trip: the page navigates away mid-flow, so
// the `AnalyticsFlow` closure dies before `success`/`failure` can fire. We
// stash the flow id under this key right before the redirect and let the
// auth-state watcher resolve the outcome when the user comes back.
const PENDING_LOGIN_KEY = 'analytics:pending_login';
// Anything older than this is treated as abandoned — the user navigated away
// and never came back to complete the OAuth round-trip.
const PENDING_LOGIN_MAX_AGE_MS = 10 * 60 * 1000;

type PendingLogin = {
    flowId: string;
    initiatedAt: number;
};

// `null` = unasked; mirrors `user_preferences.analytics_consent`.
const consent = ref<boolean | null>(null);
// True once the user has dismissed the prompt without answering. Survives
// page reloads via localStorage so we don't pester them every refresh.
const hasDeferred = ref(false);
const hasPromptedThisSession = ref(false);
let watchersInstalled = false;
let lastReconciledUserId: string | null = null;
// Stitching is idempotent server-side (filter is `user_id IS NULL`), but the
// once-per-user-per-session flag spares the round-trip on subsequent
// reconciles within the same tab.
let lastStitchedUserId: string | null = null;

export type AnalyticsFlow = {
    flowId: string;
    intent: (properties?: Record<string, unknown>) => void;
    success: (properties?: Record<string, unknown>) => void;
    failure: (properties?: Record<string, unknown>) => void;
};

export function useAnalytics(serviceOverride?: AnalyticsService) {
    const service = serviceOverride ?? analyticsService;
    const userStore = useUserStore();
    const modal = useModal();

    function installAuthWatcher(): void {
        // Idempotent: app-lifetime hook installed from main.ts on boot.
        if (watchersInstalled) {
            return;
        }
        watchersInstalled = true;
        loadAnonConsent();
        applyServiceEnablement();
        // Resolve any Discord OAuth round-trip outcome left over from before
        // the redirect. Awaits the user store internally so we can tell a
        // successful return from a denied/abandoned one.
        void resolvePendingDiscordLogin();
        watch(
            () => userStore.user?.id ?? null,
            (newId, oldId) => {
                if (newId != null && newId !== oldId) {
                    void reconcileConsent(newId);
                } else if (newId == null && oldId != null) {
                    lastReconciledUserId = null;
                    lastStitchedUserId = null;
                    loadAnonConsent();
                    applyServiceEnablement();
                }
            },
            { immediate: true },
        );
    }

    async function resolvePendingDiscordLogin(): Promise<void> {
        const pending = readPendingLogin();
        if (pending == null) {
            return;
        }

        // Block on the user-store boot so we can tell a still-loading state
        // from a genuinely-unauthed return. `isUserResolvedPromise` settles
        // exactly once per session (userStore.onMounted).
        await Promise.resolve(userStore.isUserResolvedPromise).catch(() => undefined);

        // Re-read under the await: another tab / a duplicate install path
        // may have cleared it. We always clear from the side that fires.
        const stillPending = readPendingLogin();
        if (stillPending == null) {
            return;
        }
        clearPendingLogin();

        const isStale = Date.now() - stillPending.initiatedAt > PENDING_LOGIN_MAX_AGE_MS;
        const isAuthed = userStore.user?.id != null;

        // Authed-but-stale: the user is signed in, but the pending entry is
        // too old to safely credit Discord — they likely signed in via some
        // other path in the meantime. Drop without attribution.
        if (isAuthed && isStale) {
            return;
        }

        const resumed = resumeFlow('login', stillPending.flowId);
        if (isAuthed) {
            resumed.success({ method: 'discord' });
        } else {
            resumed.failure({
                method: 'discord',
                reason: isStale ? 'oauth_abandoned' : 'oauth_failed',
            });
        }
    }

    // One-shot load: refreshes `consent.value` from whichever source applies
    // right now (server when authed, localStorage when not). Useful for code
    // paths that render consent state but boot before the auth watcher is
    // installed (e.g. the Settings page mounted in a unit test).
    async function loadConsent(): Promise<void> {
        const userId = userStore.user?.id;
        if (userId != null) {
            await reconcileConsent(userId);
        } else {
            loadAnonConsent();
            applyServiceEnablement();
        }
    }

    function loadAnonConsent(): void {
        const raw = readAnonConsent();
        if (raw === 'granted') {
            consent.value = true;
            hasDeferred.value = false;
        } else if (raw === 'declined') {
            consent.value = false;
            hasDeferred.value = false;
        } else if (raw === 'deferred') {
            consent.value = null;
            hasDeferred.value = true;
        } else {
            consent.value = null;
            hasDeferred.value = false;
        }
    }

    async function reconcileConsent(userId: string): Promise<void> {
        if (lastReconciledUserId === userId) {
            return;
        }
        lastReconciledUserId = userId;

        const localRaw = readAnonConsent();

        try {
            const prefs = await getUserPreferences(userId);
            const serverConsent = prefs.analyticsConsent;

            if (serverConsent != null) {
                // Server has an answer; it wins. Overwrite local so the two
                // never drift. A stale `'deferred'` marker is now moot.
                consent.value = serverConsent;
                hasDeferred.value = false;
                writeAnonConsent(serverConsent ? 'granted' : 'declined');
            } else if (localRaw === 'granted') {
                await setAnalyticsConsent(userId, true);
                consent.value = true;
                hasDeferred.value = false;
            } else if (localRaw === 'declined') {
                await setAnalyticsConsent(userId, false);
                consent.value = false;
                hasDeferred.value = false;
            } else if (localRaw === 'deferred') {
                // Local-only deferral; do not propagate to the server. Keep
                // the marker so the next track call doesn't re-prompt.
                consent.value = null;
                hasDeferred.value = true;
            } else {
                consent.value = null;
                hasDeferred.value = false;
            }
        } catch (error) {
            logError(error, { source: 'analytics:reconcile-consent' });
        } finally {
            applyServiceEnablement();
        }

        if (consent.value === true) {
            await stitchIfNeeded(userId);
        }
    }

    function applyServiceEnablement(): void {
        service.setEnabled(consent.value === true);
    }

    async function stitchIfNeeded(userId: string): Promise<void> {
        if (lastStitchedUserId === userId) {
            return;
        }
        const anonymousId = service.getAnonymousId();
        if (anonymousId == null) {
            // No anon trail in this browser — nothing to stitch.
            return;
        }
        try {
            await stitchIdentity(anonymousId);
            lastStitchedUserId = userId;
        } catch (error) {
            logError(error, { source: 'analytics:stitch-identity' });
        }
    }

    function ensureConsentDecision(): boolean {
        if (consent.value === true) {
            return true;
        }
        if (consent.value === false) {
            return false;
        }
        // `null`: never asked, or asked-and-dismissed. The latter persists in
        // localStorage so we don't re-prompt every reload.
        if (hasDeferred.value) {
            return false;
        }
        if (!hasPromptedThisSession.value) {
            hasPromptedThisSession.value = true;
            modal.showModal(ModalCode.AnalyticsConsent, {
                onDecision: (allowed: boolean) => {
                    void saveConsent(allowed);
                },
                onDismiss: () => {
                    deferConsent();
                },
            });
        }
        return false;
    }

    function deferConsent(): void {
        hasDeferred.value = true;
        writeAnonConsent('deferred');
    }

    async function saveConsent(allowed: boolean): Promise<void> {
        consent.value = allowed;
        hasDeferred.value = false;

        const userId = userStore.user?.id;
        if (userId != null) {
            try {
                await setAnalyticsConsent(userId, allowed);
            } catch (error) {
                logError(error, { source: 'analytics:save-consent' });
            }
        }
        writeAnonConsent(allowed ? 'granted' : 'declined');

        applyServiceEnablement();

        if (allowed && userId != null) {
            await stitchIfNeeded(userId);
        }
    }

    // Defense-in-depth: `service.track` already swallows its own failures, but
    // `ensureConsentDecision` opens a modal and reads reactive state — if any
    // of that throws, the customer-facing caller (LoginModal, GameForm, …)
    // would see the exception as a wrongful "save failed". Analytics never
    // surfaces here.
    function trackImpression(
        eventName: string,
        properties?: Record<string, unknown>,
        options: { dedupeKey?: string } = {},
    ): void {
        try {
            if (!ensureConsentDecision()) {
                return;
            }
            service.track({
                eventName,
                eventType: 'impression',
                properties,
                dedupeKey: options.dedupeKey,
            });
        } catch (error) {
            logError(error, { source: 'analytics:track-impression' });
        }
    }

    function trackInteraction(eventName: string, properties?: Record<string, unknown>): void {
        try {
            if (!ensureConsentDecision()) {
                return;
            }
            service.track({
                eventName,
                eventType: 'interaction',
                properties,
            });
        } catch (error) {
            logError(error, { source: 'analytics:track-interaction' });
        }
    }

    function track(
        eventName: string,
        eventType: AnalyticsEventType,
        properties?: Record<string, unknown>,
        options: { dedupeKey?: string; flowId?: string } = {},
    ): void {
        try {
            if (!ensureConsentDecision()) {
                return;
            }
            service.track({
                eventName,
                eventType,
                properties,
                dedupeKey: options.dedupeKey,
                flowId: options.flowId,
            });
        } catch (error) {
            logError(error, { source: 'analytics:track' });
        }
    }

    function resumeFlow(eventName: string, flowId: string): AnalyticsFlow {
        // Used when the flow_id was minted in a previous JS context (e.g.
        // before an OAuth redirect) and we now want to resolve its outcome.
        // No new id is minted — the caller supplies one so funnel queries
        // can join the prior `intent` to this `success`/`failure`.
        return {
            flowId,
            intent: (properties) => track(eventName, 'intent', properties, { flowId }),
            success: (properties) => track(eventName, 'success', properties, { flowId }),
            failure: (properties) => track(eventName, 'failure', properties, { flowId }),
        };
    }

    function startFlow(eventName: string): AnalyticsFlow {
        // One flow_id ties intent → success/failure across navigations.
        // The id is minted regardless of consent so the closure stays usable
        // if consent flips on between intent and outcome.
        //
        // `crypto.randomUUID` throws on insecure contexts and ancient
        // browsers. `startFlow` runs at component setup time (LoginModal,
        // GameForm, …), so a throw here would block the modal from mounting.
        // Fall back to a no-op flow so the UI keeps working.
        let flowId: string;
        try {
            flowId = crypto.randomUUID();
        } catch (error) {
            logError(error, { source: 'analytics:start-flow' });
            return {
                flowId: '',
                intent: () => undefined,
                success: () => undefined,
                failure: () => undefined,
            };
        }
        return resumeFlow(eventName, flowId);
    }

    return {
        consent,
        installAuthWatcher,
        loadConsent,
        saveConsent,
        trackImpression,
        trackInteraction,
        track,
        startFlow,
        resumeFlow,
    };
}

function readAnonConsent(): AnonConsentString | null {
    try {
        const raw = localStorage.getItem(ANON_CONSENT_KEY);
        if (raw === 'granted' || raw === 'declined' || raw === 'deferred') {
            return raw;
        }
        return null;
    } catch {
        return null;
    }
}

function writeAnonConsent(value: AnonConsentString): void {
    try {
        localStorage.setItem(ANON_CONSENT_KEY, value);
    } catch {
        // Storage may be disabled (private mode, quota); we're best-effort.
    }
}

function readPendingLogin(): PendingLogin | null {
    try {
        const raw = localStorage.getItem(PENDING_LOGIN_KEY);
        if (raw == null) {
            return null;
        }
        const parsed = JSON.parse(raw) as Partial<PendingLogin>;
        if (typeof parsed?.flowId !== 'string' || typeof parsed?.initiatedAt !== 'number') {
            return null;
        }
        return { flowId: parsed.flowId, initiatedAt: parsed.initiatedAt };
    } catch {
        return null;
    }
}

export function writePendingLogin(value: PendingLogin): void {
    try {
        localStorage.setItem(PENDING_LOGIN_KEY, JSON.stringify(value));
    } catch {
        // Storage disabled — pending tracking quietly degrades to no-op.
    }
}

export function clearPendingLogin(): void {
    try {
        localStorage.removeItem(PENDING_LOGIN_KEY);
    } catch {
        // ignore
    }
}

// Test-only — exposes the module-level state for assertions without leaking
// it into the public composable surface.
export const __testing__ = {
    resetConsentState(): void {
        consent.value = null;
        hasDeferred.value = false;
        hasPromptedThisSession.value = false;
        watchersInstalled = false;
        lastReconciledUserId = null;
        lastStitchedUserId = null;
    },
    setConsent(value: boolean | null): void {
        consent.value = value;
    },
    getHasPromptedThisSession(): boolean {
        return hasPromptedThisSession.value;
    },
    setHasPromptedThisSession(value: boolean): void {
        hasPromptedThisSession.value = value;
    },
    getHasDeferred(): boolean {
        return hasDeferred.value;
    },
    setHasDeferred(value: boolean): void {
        hasDeferred.value = value;
    },
};
